import { useState } from 'react';
import { Row } from '@tanstack/react-table';
import classNames from 'classnames';

import { AmendmentDetailsModal } from '@xemplo/amendment-details-modal';
import { useGetAmendmentsQuery } from '@xemplo/amendment-query';
import { CreateAmendment } from '@xemplo/create-amendment';
import { AmendmentListItem, AmendmentStatusIds, SortDirection } from '@xemplo/gp-types';
import { transformStatus } from '@xemplo/gp-utils';
import { IconButton, IconButtonSize } from '@xemplo/icon-button';
import { DawnAddPlus } from '@xemplo/icons';
import { useModalDialog } from '@xemplo/modal-dialog';

import { columns } from './amendment-list-widget.helper';
import * as S from './amendment-list-widget.styles';

export function AmendmentListWidget() {
  const [amendmentId, setAmendmentId] = useState('');
  const { dialogProps, openModalDialog, closeDialog, isOpen } = useModalDialog({
    id: 'amendment-details-modal',
  });
  const { data, isLoading } = useGetAmendmentsQuery({
    requestParams: {
      per_page: 8,
      page: 1,
      sort: { requestedDate: SortDirection.DESC },
      filter: {
        statusId: transformStatus([
          AmendmentStatusIds.CREATED,
          AmendmentStatusIds.REJECTED,
        ]).toString(),
        showComplete: false,
        showArchived: false,
      },
    },
  });

  const handleRowClick = (
    e: React.MouseEvent<Element, MouseEvent>,
    row: Row<AmendmentListItem>,
  ) => {
    setAmendmentId(row.original.amendmentId);
    openModalDialog();
  };

  const count = data?.result?.rows.length;

  return (
    <>
      <AmendmentDetailsModal
        amendmentId={amendmentId}
        isOpen={isOpen}
        dialogProps={dialogProps}
        onClose={closeDialog}
      />
      <S.CustomWidget
        title="Amendments"
        count={count}
        className={classNames({ 'is-empty': !count })}
        button={
          <CreateAmendment
            renderButton={(onClick) => (
              <IconButton
                id="create-new-amendment"
                ariaLabel="Create new amendment button"
                onClick={onClick}
                size={IconButtonSize.Small}
              >
                <DawnAddPlus fontSize={16} />
              </IconButton>
            )}
          />
        }
      >
        {!!count && <S.SubTitle>Recently added</S.SubTitle>}
        <S.AmendmentListTable
          tableOverflowControlMinWidth={312}
          data={data?.result?.rows ?? []}
          columns={columns}
          enablePagination={false}
          enableSorting={false}
          enableRowSelection={false}
          pageCount={data?.result?.total_Pages ?? 0}
          totalRowCount={data?.result?.total ?? 0}
          isLoading={isLoading}
          onBodyRowClick={handleRowClick}
          noResultProps={{ message: 'You are up to date!' }}
        />
      </S.CustomWidget>
    </>
  );
}

export default AmendmentListWidget;
